// Recent generated-reports table.
//
// Extracted from Reports.tsx during the Maintainability Sprint 2026-04-19
// (Task 1.2 final squeeze). Owns the column definitions, the empty state,
// and the AdminDataTable wrapper.
import { useMemo } from "react";
import { ClipboardList, Download } from "lucide-react";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import type { GeneratedReport } from "@/lib/admin-api/finance";
import { REPORT_STATUS_COLORS, REPORT_STATUS_LABELS } from "./constants";
import { formatDate } from "./formatters";

interface RecentReportsTableProps {
  readonly recent: readonly GeneratedReport[];
  readonly isLoading: boolean;
}

export default function RecentReportsTable({
  recent,
  isLoading,
}: RecentReportsTableProps) {
  const columns: readonly ColumnDef<GeneratedReport>[] = useMemo(
    () => [
      {
        id: "template_name",
        header: "Rapport",
        cell: (r) => (
          <span className="text-sm font-medium text-stone-900">
            {r.template_name}
          </span>
        ),
      },
      {
        id: "period",
        header: "Periode",
        cell: (r) => (
          <span className="text-sm text-stone-600">{r.period}</span>
        ),
      },
      {
        id: "status",
        header: "Statut",
        cell: (r) => (
          <div className="flex items-center gap-2">
            {r.status === "generating" && (
              <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
            )}
            <span
              className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${REPORT_STATUS_COLORS[r.status] ?? ""}`}
            >
              {REPORT_STATUS_LABELS[r.status] ?? r.status}
            </span>
          </div>
        ),
      },
      {
        id: "created_at",
        header: "Cree le",
        cell: (r) => (
          <span className="text-sm text-stone-500">
            {formatDate(r.created_at)}
          </span>
        ),
      },
      {
        id: "file",
        header: "Telecharger",
        cell: (r) =>
          r.status === "ready" && r.file_url ? (
            <a
              href={r.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
            >
              <Download className="h-3.5 w-3.5" />
              Telecharger
            </a>
          ) : (
            <span className="text-xs text-stone-400">--</span>
          ),
      },
    ],
    [],
  );

  return (
    <div>
      <h2 className="mb-4 text-sm font-semibold text-stone-700">
        Rapports recents
      </h2>
      {!isLoading && recent.length === 0 ? (
        <EmptyState
          icon={<ClipboardList className="h-6 w-6" />}
          title="Aucun rapport genere"
          description="Vos rapports generes apparaitront ici."
        />
      ) : (
        <AdminDataTable
          data={recent}
          columns={columns}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}
